import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articleDetailData, type ArticleContent } from '@/mocks/articleData';
import { hotArticles } from '@/mocks/homeData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<ArticleContent | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (slug && articleDetailData[slug]) {
      setArticle(articleDetailData[slug]);
      setNotFound(false);
    } else if (slug) {
      setNotFound(true);
      setArticle(null);
    }
  }, [slug]);

  if (notFound) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center px-4">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-xl font-semibold text-foreground-950 mb-2">文章不存在</h1>
            <p className="text-sm text-foreground-600 mb-6">该文章可能已被移除或链接有误</p>
            <Link to="/" className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticlesData = (article.relatedArticles || [])
    .map((id) => hotArticles.find((a) => a.id === id) || null)
    .filter(Boolean);

  return (
    <>
      <Navbar />
      <main>
        {/* Breadcrumb */}
        <div className="pt-20 md:pt-24 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 py-3 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link>
              <i className="ri-arrow-right-s-line text-foreground-400"></i>
              <span className="text-foreground-950 font-medium truncate max-w-[200px]">{article.title}</span>
            </nav>
          </div>
        </div>

        <article className="w-full py-8 md:py-12 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-8 md:gap-10">
              {/* Main Content */}
              <div>
                <div className="mb-6">
                  <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-3">
                    {article.category}
                  </span>
                  <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-4">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
                    <span className="flex items-center gap-1">
                      <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-user-3-line text-xs text-primary-600"></i>
                      </div>
                      {article.expert} · {article.expertTitle}
                    </span>
                    <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
                    <span><i className="ri-eye-line mr-1"></i>{article.views} 阅读</span>
                    <span>{article.publishDate}</span>
                  </div>
                </div>

                <div className="relative aspect-video rounded-xl overflow-hidden mb-8">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 cursor-pointer group">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-primary-500/90 flex items-center justify-center group-hover:bg-primary-500 transition-colors">
                      <i className="ri-play-fill text-background-50 text-2xl md:text-3xl ml-0.5"></i>
                    </div>
                  </div>
                </div>

                <div
                  className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed space-y-4"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />

                {/* FAQs within article */}
                {article.faqs && article.faqs.length > 0 && (
                  <div className="mt-10 pt-8 border-t border-background-200">
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-foreground-950 mb-4">
                      常见疑问
                    </h3>
                    <div className="space-y-3">
                      {article.faqs.map((faq, i) => (
                        <div key={i} className="bg-background-100 rounded-lg p-4 md:p-5">
                          <p className="font-medium text-foreground-950 text-sm md:text-base mb-2">
                            <i className="ri-question-answer-line text-primary-500 mr-2"></i>
                            {faq.question}
                          </p>
                          <p className="text-sm text-foreground-600">{faq.answer}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="hidden lg:block">
                <div className="sticky top-24">
                  <h3 className="font-heading text-base font-semibold text-foreground-950 mb-4">
                    相关推荐
                  </h3>
                  <div className="space-y-4">
                    {relatedArticlesData.map((item) => (
                      <Link
                        key={item.id}
                        to={`/article/${item.id}`}
                        className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors cursor-pointer group"
                      >
                        <img
                          src={item.coverImage}
                          alt={item.title}
                          className="w-20 h-14 rounded-lg object-cover shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-foreground-950 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                            {item.title}
                          </p>
                          <span className="text-xs text-foreground-500 mt-1 block">{item.views} 阅读</span>
                        </div>
                      </Link>
                    ))}
                  </div>

                  <div className="mt-8 p-5 rounded-xl bg-accent-100/70">
                    <div className="w-10 h-10 rounded-full bg-accent-500 flex items-center justify-center mb-3">
                      <i className="ri-information-line text-background-50 text-lg"></i>
                    </div>
                    <p className="text-sm text-accent-900 font-medium mb-2">温馨提示</p>
                    <p className="text-xs text-accent-700 leading-relaxed">
                      本文内容仅供科普参考，不构成医疗建议。如有健康疑虑，请及时咨询专业医师。
                    </p>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}